import { api } from '@/api/client';
import type { MessengerMessage, MessengerThread } from '@/api/client';

export type UnreadOverlayItem = {
  msg: MessengerMessage;
  thread: MessengerThread;
};

/** Состояние одного треда в оверлее: как в чате — страницы сливаются, hasMore для «ещё». */
export type UnreadOverlayThreadLoad = {
  thread: MessengerThread;
  lastReadMessageId: string | null;
  /** Слитые страницы по возрастанию времени (как в API после reverse) */
  loadedMessages: MessengerMessage[];
  hasMore: boolean;
};

/**
 * Входящие непрочитанные (от собеседников) после last_read_message_id в загруженном срезе.
 */
export function extractUnreadPeerMessages(
  messages: MessengerMessage[],
  lastReadMessageId: string | null,
  currentUserPersonId: string
): MessengerMessage[] {
  const isPeer = (m: MessengerMessage) =>
    m.sender_id !== currentUserPersonId && !m.deleted_at;

  if (!lastReadMessageId) {
    return messages.filter(isPeer);
  }

  const idx = messages.findIndex((m) => m.id === lastReadMessageId);
  if (idx >= 0) {
    return messages.slice(idx + 1).filter(isPeer);
  }

  return messages.filter(isPeer);
}

export function buildOverlayItemsFromLoads(
  loads: Record<string, UnreadOverlayThreadLoad>,
  currentUserPersonId: string
): UnreadOverlayItem[] {
  const out: UnreadOverlayItem[] = [];
  for (const st of Object.values(loads)) {
    const peerUnread = extractUnreadPeerMessages(
      st.loadedMessages,
      st.lastReadMessageId,
      currentUserPersonId
    );
    for (const msg of peerUnread) {
      out.push({ msg, thread: st.thread });
    }
  }
  out.sort(
    (a, b) => new Date(a.msg.created_at).getTime() - new Date(b.msg.created_at).getTime()
  );
  return out;
}

/** Первая страница по каждому треду с непрочитанным (как GET messages без before). */
export async function fetchInitialUnreadThreadLoads(): Promise<Record<string, UnreadOverlayThreadLoad>> {
  const { threads } = await api.messenger.getThreads();
  const unreadThreads = threads.filter(
    (t) => (t.unread_count ?? 0) > 0 && t.last_message_at?.trim()
  );
  const out: Record<string, UnreadOverlayThreadLoad> = {};

  for (const thread of unreadThreads) {
    const data = await api.messenger.getMessages(thread.id, { limit: 100 });
    out[thread.id] = {
      thread,
      lastReadMessageId: data.last_read_message_id,
      loadedMessages: data.messages,
      hasMore: data.hasMore,
    };
  }

  return out;
}

/**
 * Подгружает по одной странице старых сообщений только там, где ещё не хватает
 * непрочитанных относительно unread_count (hasMore при большой истории не повод грузить зря).
 */
export async function loadMoreUnreadForAllPendingThreads(
  loads: Record<string, UnreadOverlayThreadLoad>,
  currentUserPersonId: string
): Promise<Record<string, UnreadOverlayThreadLoad>> {
  const entries = Object.entries(loads).filter(([, st]) => {
    if (!st.hasMore || st.loadedMessages.length === 0) return false;
    const loadedUnread = extractUnreadPeerMessages(
      st.loadedMessages,
      st.lastReadMessageId,
      currentUserPersonId
    ).length;
    const expected = st.thread.unread_count ?? 0;
    return loadedUnread < expected;
  });
  if (entries.length === 0) return loads;

  const next = { ...loads };

  await Promise.all(
    entries.map(async ([tid, st]) => {
      const oldest = st.loadedMessages[0]!;
      const data = await api.messenger.getMessages(tid, { limit: 100, before: oldest.id });
      next[tid] = {
        ...st,
        loadedMessages: [...data.messages, ...st.loadedMessages],
        hasMore: data.hasMore,
      };
    })
  );

  return next;
}
